'use client';

import cx from 'classnames';
import { useIntl } from 'react-intl';

import {
    useExperimentVariation,
    useTrackPageViewAmplitude,
} from '../../../../core/Tracking/Amplitude/Amplitude';
import { FlightCancellationOfferBanner } from '../../../../design-system/components/flight-cancellation-offer-banner/FlightCancellationOfferBanner';
import { HeadbandMessage } from '../../../../design-system/components/headband/HeadbandMessage';
import { WemapProvider } from '../../../../design-system/components/wemap/WemapProvider';
import {
    Breadcrumb,
    useFacetBreadcrumbItems,
} from '../../../../domains/Layout/Breadcrumb/Breadcrumb';
import { Footer } from '../../../../domains/Layout/Footer/Footer';
import { imageLoaderForId } from '../../../../domains/Layout/Image/URL/ImageURL';
import { PrimaryNav } from '../../../../domains/Layout/PrimaryNav/PrimaryNav';
import { WhereToGoSection } from '../../../../domains/WhereToGo/WhereToGoSection/WhereToGoSection';
import { PointOfInterestCarousel } from '../../../../partials/shared/components/PointOfInterestCarousel/PointOfInterestCarousel';
import { sortedAgenciesByBetterTripIndex } from '../../../../resources/agency/domain/operations/sortedAgenciesByBetterTripIndex';
import { hasClosingSignal } from '../../../../resources/destination/domain/operations/hasClosingSignal';
import { mapItineraryCardTagsToTotalCounts } from '../../../../resources/itinerary/domain/operations/getAmplitudeTags';
import { AgencyListSection } from '../../../../shared/components/AgencyListSection/AgencyListSection';
import { COVER_HEIGHT } from '../../../../shared/components/Cover/Cover';
import FamilyMiniBookSection from '../../../../shared/components/FamilyMiniBookSection/FamilyMiniBookSection';
import { FamilyReassuranceStats } from '../../../../shared/components/FamilyReassurance/FamilyReassuranceStats/FamilyReassuranceStats';
import { OtherDestinationsSection } from '../../../../shared/components/OtherDestinationsSection/OtherDestinationSection';
import { RegionCarouselSection } from '../../../../shared/components/RegionCarouselSection/RegionCarouselSection';
import { TripReviewListDestinationSection } from '../../../../shared/components/TripReviewListDestinationSection/TripReviewListDestinationSection';
import { UGCHighlightSection } from '../../../../shared/components/UGCHighlightSection/UGCHighlightSection';
import { WhenToGoSection } from '../../../../shared/components/WhenToGoSection/WhenToGoSection';
import { useFeatureFlag } from '../../../../shared/hooks/useFeatureFlag';
import { useLocale } from '../../../../shared/hooks/useLocale';
import { useUrlGenerator } from '../../../../shared/hooks/useUrlGenerator';

import { Cover } from './shared/components/Cover/Cover';
import { Itineraries } from './shared/components/Itineraries';
import { QuestionAnswers } from './shared/components/QuestionAnswers';
import { SuggestedFacets } from './shared/components/SuggestedFacets';
import { WhereToGoSectionDescription } from './shared/components/WhereToGoSectionDescription/WhereToGoSectionDescription';
import { PageFacetData } from './shared/utilities/transformPageData';

import style from './PageView.module.css';

export function PageView(props: PageFacetData) {
    const { formatMessage } = useIntl();
    const { locale } = useLocale();
    const { generateUrl } = useUrlGenerator();
    const { hasFeature } = useFeatureFlag();

    const {
        facet,
        facetContents,
        primaryNav,
        itineraries,
        tripReviews,
        suggestedFacets,
        destinationItinerariesCount,
        regions,
        destinationsTypeRegion,
        otherDestinations,
        averageRating,
        ugcHighlights,
        mainWhenToGo,
        pointsOfInterest,
    } = props;
    const { destination } = facet;
    const { breadcrumbItems } = useFacetBreadcrumbItems({ destination, facet });
    const agencies = sortedAgenciesByBetterTripIndex(
        itineraries
            .map((itinerary) => {
                return itinerary.agency;
            })
            .reduce<PageFacetData['itineraries'][0]['agency'][]>((uniqueAgencies, agency) => {
                if (!uniqueAgencies.find(({ id }) => id === agency.id)) {
                    uniqueAgencies.push(agency);
                }

                return uniqueAgencies;
            }, []),
    );
    const highlightedRegions = regions.filter((region) => {
        return region.isHighlighted;
    });

    const whenToGoTitle =
        facetContents.whenToGo?.title ??
        formatMessage(
            { id: 'page.facet.whenToGo.title' },
            {
                destinationPreposition: destination.preposition,
                destinationName: destination.name,
            },
        );

    const tripReviewsTitle = {
        as: 'div',
        children: formatMessage({ id: 'global.component.tripReviewListSection.subTitle' }),
    } as const;

    const tripReviewsSubTitle = {
        as: 'div',
        scale: 'primary-l-extra-bold',
        children: formatMessage(
            { id: 'global.component.tripReviewListSection.title' },
            {
                destinationPreposition: destination.preposition,
                destinationName: destination.name,
            },
        ),
    } as const;

    const whereToGoTitle = facetContents.whereToGo
        ? facetContents.whereToGo.title
        : facetContents.mainContent?.title;
    const whereToGoContent = facetContents.whereToGo
        ? facetContents.whereToGo.content
        : facetContents.mainContent?.content;
    const whereToGoContentReferences = facetContents.whereToGo
        ? facetContents.whereToGo.contentReferences
        : facetContents.mainContent?.contentReferences;

    useTrackPageViewAmplitude((ampli, defaultProperties) => {
        ampli.viewedPageFacet({
            ...defaultProperties,
            destination_id: destination.id,
            destination_slug: destination.slug,
            has_trip_form_available: destination.isAvailableForQuoteRequest,
            facet_id: facet.id,
            facet_tag_category: facet.filter.name,
            facet_tag_name:
                typeof facet.filter.value === 'boolean' ? facet.filter.name : facet.filter.value,
            nb_itineraries_available: itineraries.length,
            nb_questions_displayed: facetContents.questionAnswers.length,
            has_alert_message: hasClosingSignal(destination),
            nb_tags_list: mapItineraryCardTagsToTotalCounts(itineraries),
        });
    });

    const ABPathfinderExperimentFacetTripDesigner = useExperimentVariation(
        'fr-x-pathfinder-facet-trip-designer',
        {
            destination_slug: destination.slug,
            facet_tag_name:
                typeof facet.filter.value === 'boolean' ? facet.filter.name : facet.filter.value,
        },
    );
    const isABPathfinderExperimentFacetTripDesigner =
        ABPathfinderExperimentFacetTripDesigner?.value === 'variation';

    const isFamilyFacet = facet.filter.name === 'profile' && facet.filter.value === 'family';
    const showFamilyMiniBookSection = isFamilyFacet && hasFeature('family_minibook');
    const [localePrefix] = locale.split('-');
    const familyMiniBookTagSource = `${localePrefix}_minibookfamily_2025_facetpage`;
    const coverImageId = parseInt(facet?.coverImage?.id || destination.coverPicture.id, 10);
    const coverCopyright = facet?.coverImage?.author || destination.coverPicture.author;

    return (
        <>
            {destination.alertMessage ? (
                <HeadbandMessage message={destination.alertMessage} />
            ) : null}

            <PrimaryNav contextContinent={destination.continent} {...primaryNav} />

            <div className={cx('layout-with-centered-content', style.mainWrapper)}>
                <section className={cx('main-container-cover', style.coverSection)}>
                    <Cover
                        image={{
                            alt: '',
                            src: coverImageId.toString(),
                            loader: imageLoaderForId({
                                fixedHeight: COVER_HEIGHT,
                            }),
                            quality: 80,
                            sizes: '(max-width: 640px) 100vw, (max-width: 1024px) 60vw, 50vw',
                        }}
                        copyright={coverCopyright}
                        facet={facet}
                        destination={facet.destination}
                        trackingInformation={{
                            tripFormContactAgency: {
                                block: 'cartouche',
                                facet_id: facet.id,
                                destination_id: destination.id,
                                destination_slug: destination.slug,
                            },
                        }}
                        destinationHasItinerary={destinationItinerariesCount > 0}
                        isFamilyFacet={isFamilyFacet}
                    />
                    <FlightCancellationOfferBanner />
                </section>

                <section className={cx('main-container', style.breadcrumbSection)}>
                    <Breadcrumb items={breadcrumbItems} />
                </section>

                {itineraries.length > 0 ? (
                    <section className={cx('main-container', style.section)}>
                        <Itineraries
                            itineraries={itineraries}
                            destination={destination}
                            destinationItineraryCount={destinationItinerariesCount}
                            facetContents={facetContents}
                            facet={facet}
                            agencyName={(agency) =>
                                formatMessage(
                                    { id: 'global.component.itineraryCard.mainAgentName' },
                                    { name: agency.name },
                                )
                            }
                            isABTripDesigner={isABPathfinderExperimentFacetTripDesigner}
                        />
                    </section>
                ) : null}

                {isFamilyFacet ? (
                    <section className={cx('main-container', style.section)}>
                        <FamilyReassuranceStats showLogo={true} />
                    </section>
                ) : null}
            </div>

            {agencies.length > 0 ? (
                <div className={style.agenciesBackground}>
                    <div className={cx('layout-with-centered-content', style.agenciesWrapper)}>
                        <section className="main-container">
                            <AgencyListSection
                                destination={destination}
                                agencies={agencies}
                                subTitleElement="div"
                                agencyName={(agency) =>
                                    formatMessage(
                                        { id: 'global.component.agencyCard.teamName' },
                                        { name: agency.name },
                                    )
                                }
                            />
                        </section>
                    </div>
                </div>
            ) : null}

            {showFamilyMiniBookSection ? (
                <div className={cx('layout-with-centered-content', style.agenciesWrapper)}>
                    <section className="main-container">
                        <FamilyMiniBookSection
                            destination={destination}
                            tagSource={familyMiniBookTagSource}
                        />
                    </section>
                </div>
            ) : null}

            {destinationsTypeRegion.length > 0 || highlightedRegions.length > 0 ? (
                <div className={style.regionsWrapper}>
                    <div className="layout-with-centered-content">
                        <section className="main-container">
                            <RegionCarouselSection
                                title={formatMessage(
                                    { id: 'page.destination.regions.title' },
                                    {
                                        destinationPreposition: destination.preposition,
                                        destinationName: destination.name,
                                    },
                                )}
                                destinationsTypeRegion={destinationsTypeRegion}
                                regions={highlightedRegions}
                                titleElement="div"
                                regionNameElement="span"
                            />
                        </section>
                    </div>
                </div>
            ) : null}

            <div className={cx('layout-with-centered-content', style.bottomWrapper)}>
                {whereToGoTitle && whereToGoContent && whereToGoContentReferences ? (
                    <>
                        <WemapProvider>
                            <WhereToGoSection
                                pointsOfInterest={whereToGoContentReferences.pointOfInterests}
                                regions={regions}
                                destination={destination}
                                destinationsTypeRegion={destinationsTypeRegion}
                                secondaryRegionTitleElement="div"
                                regionNameElement="span"
                            />
                        </WemapProvider>

                        <section className={cx('main-container', style.whereToGoSection)}>
                            <div className="row">
                                <div className="col-12 col-lg-6">
                                    <WhereToGoSectionDescription
                                        title={whereToGoTitle}
                                        content={whereToGoContent}
                                    />
                                </div>
                            </div>
                        </section>
                    </>
                ) : null}

                {pointsOfInterest.length > 0 ? (
                    <section className={cx('main-container', style.section)}>
                        <PointOfInterestCarousel pointsOfInterest={pointsOfInterest} />
                    </section>
                ) : null}

                {ugcHighlights.length > 0 ? (
                    <section className={cx('main-container', style.section)}>
                        <UGCHighlightSection
                            ugcHighlights={ugcHighlights}
                            destination={destination}
                        />
                    </section>
                ) : null}
            </div>

            {mainWhenToGo && facetContents.whenToGo ? (
                <div className={cx('layout-with-centered-content', style.whenToGoWrapper)}>
                    <section className={cx('main-container', style.whenToGoSection)}>
                        <WhenToGoSection
                            destination={destination}
                            content={facetContents.whenToGo.content}
                            title={whenToGoTitle}
                            subTitleElement="h2"
                            cta={{
                                title: whenToGoTitle,
                                label: formatMessage({ id: 'global.cta.seeWhenToGo' }),
                                url: generateUrl('destination_when_to_go', {
                                    destinationSlug: destination.slug,
                                }),
                            }}
                        />
                    </section>
                </div>
            ) : null}

            <div className={cx('layout-with-centered-content', style.bottomWrapper)}>
                {facetContents.questionAnswers.length > 0 ? (
                    <section className={cx('main-container', style.section)}>
                        <QuestionAnswers questionAnswers={facetContents.questionAnswers} />
                    </section>
                ) : null}

                {tripReviews.length > 0 && averageRating ? (
                    <section id="reviews" className={cx('main-container', style.section)}>
                        <TripReviewListDestinationSection
                            averageRating={averageRating}
                            destination={destination}
                            tripReviews={tripReviews}
                            title={tripReviewsTitle}
                            subtitle={tripReviewsSubTitle}
                        />
                    </section>
                ) : null}

                {suggestedFacets.length > 0 ? (
                    <section className={cx('main-container', style.section)}>
                        <SuggestedFacets
                            suggestedFacets={suggestedFacets}
                            destination={destination}
                        />
                    </section>
                ) : null}

                {otherDestinations.length > 0 ? (
                    <section className={cx('main-container', style.section)}>
                        <OtherDestinationsSection
                            destinations={otherDestinations}
                            title={facetContents.otherDestinationsSectionTitle ?? undefined}
                        />
                    </section>
                ) : null}
            </div>

            <Footer
                destination={destination}
                topDestinations={primaryNav.destinationsMenu?.world.topDestinations ?? []}
            />
        </>
    );
}
